import noop from "lodash/noop";

import { useTranslate } from "@similarweb/i18n-provider";
import { Delete, Edit, Star, StarFull } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";

import type { IChosenItem } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { AUTOCOMPLETE_Z_INDEX, TOOLTIP_DELAY_MS } from "app/modules/project-from-comparison/config";
import { projectCreationDataAutomation } from "app/modules/project-from-comparison/dataAutomation";
import { useItemActions } from "app/modules/project-from-comparison/hooks/useItemActions";
import type { DomainFromAutoselect } from "app/modules/project-from-comparison/types";
import { AutocompleteWebsitesCompareItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitesCompareItem";
import type { ISimilarSite } from "pro-features/components/Workspace/Wizard/src/types";

import * as SC from "./styles";
import {
    TOOLTIP_YOUR_MAIN_DOMAIN,
    TOOLTIP_MAKE_MAIN_DOMAIN,
    TOOLTIP_EDIT_DOMAIN,
    TOOLTIP_REMOVE_FROM_LIST,
} from "./text";

type ItemActionsProps = {
    domain: string;
    isPrimary: boolean;
    canRemove: boolean;
    excludes: IChosenItem[];
    similarSites: ISimilarSite[];
    setIcon: (item: DomainFromAutoselect) => void;
};

const { carbon } = colorsPalettes;

export const ItemActions = ({ domain, isPrimary, canRemove, excludes, similarSites, setIcon }: ItemActionsProps) => {
    const { translate } = useTranslate();
    const { onEdit, onItemKeyDown, isEdit, onMakePrimary, onOpenAutocomplete, onRemove, setIsEdit } = useItemActions(
        domain,
        isPrimary,
        setIcon,
    );

    return (
        <>
            <SC.Container data-automation={projectCreationDataAutomation.itemActions.container}>
                <Tooltip
                    content={isPrimary ? translate(TOOLTIP_YOUR_MAIN_DOMAIN) : translate(TOOLTIP_MAKE_MAIN_DOMAIN)}
                    delay={{ open: TOOLTIP_DELAY_MS }}
                >
                    <SC.IconContainer
                        onClick={onMakePrimary}
                        onKeyDown={onItemKeyDown(onMakePrimary)}
                        data-automation={projectCreationDataAutomation.itemActions.primaryButton}
                    >
                        {isPrimary ? (
                            <StarFull
                                size="xs"
                                color={carbon[300]}
                            />
                        ) : (
                            <Star
                                size="xs"
                                color={carbon[300]}
                            />
                        )}
                    </SC.IconContainer>
                </Tooltip>

                <Tooltip
                    content={translate(TOOLTIP_EDIT_DOMAIN)}
                    delay={{ open: TOOLTIP_DELAY_MS }}
                >
                    <SC.IconContainer
                        onClick={onOpenAutocomplete}
                        onKeyDown={onItemKeyDown(onOpenAutocomplete)}
                        data-automation={projectCreationDataAutomation.itemActions.editButton}
                    >
                        <Edit
                            color={carbon[300]}
                            size="xs"
                        />
                    </SC.IconContainer>
                </Tooltip>

                {canRemove ? (
                    <Tooltip
                        content={translate(TOOLTIP_REMOVE_FROM_LIST)}
                        delay={{ open: TOOLTIP_DELAY_MS }}
                    >
                        <SC.IconContainer
                            onClick={onRemove}
                            onKeyDown={onItemKeyDown(onRemove)}
                            data-automation={projectCreationDataAutomation.itemActions.removeButton}
                        >
                            <Delete
                                color={carbon[300]}
                                size="xs"
                            />
                        </SC.IconContainer>
                    </Tooltip>
                ) : null}
            </SC.Container>

            {isEdit ? (
                <AutocompleteWebsitesCompareItem
                    className="AutocompleteWebsitesCreatingProject"
                    onClick={onEdit}
                    onCloseButtonClick={noop}
                    similarSites={similarSites}
                    excludes={excludes}
                    autocompleteProps={{
                        placeholder: domain,
                        onBlur: () => setIsEdit(false),
                        zIndex: AUTOCOMPLETE_Z_INDEX,
                        top: "100%",
                    }}
                />
            ) : null}
        </>
    );
};
